import { useState, useCallback } from 'react';

import { useFetchCtx } from './FetchContext';

export const useFetchMutation = <T,>(
  url: string,
  options?: RequestInit & { responseType?: 'json' | 'text' | 'blob' | 'arrayBuffer' }
) => {
  const { fetcher } = useFetchCtx();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const trigger = useCallback(
    async (body: unknown) => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetcher(url, {
          method: 'POST',
          body: JSON.stringify(body),
          ...options,
        });

        let result;
        switch (options?.responseType) {
          case 'text':
            result = await response.text();
            break;
          case 'blob':
            result = await response.blob();
            break;
          case 'arrayBuffer':
            result = await response.arrayBuffer();
            break;
          case 'json':
          default:
            result = await response.json();
            break;
        }

        setData(result);
        return {
          headers: response.headers,
          status: response.status,
          data: result,
        };
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetcher, options, url]
  );

  return [trigger, { data, loading, error }] as const;
};
